import type { IPagamentoLoja, IPedidoDetalhe, StatusPedido } from "interfaces/loja";
import { resolveEstornoPedidoCanceladoUi } from "./pedidoEstornoUi";

/** Horas — apenas front; ajuste conforme política interna. */
export const SLA_HORAS_FATURAMENTO_ATRASO = 24;
export const SLA_HORAS_FATURAMENTO_CRITICO = 72;
export const SLA_HORAS_DEVOLUCAO_PENDENTE = 48;
export const SLA_HORAS_DEVOLUCAO_CRITICO = 120;

export type SeveridadeAlertaInteligencia = "success" | "warning" | "error";

export type AlertaOperacionalInteligencia = {
  id: string;
  severidade: SeveridadeAlertaInteligencia;
  titulo: string;
  detalhe?: string;
};

export type SugestaoOperacionalInteligencia = {
  id: string;
  texto: string;
  /** Âncora na página (ex.: `sec-acoes-pedido`). */
  anchorId?: string;
};

export type SaudePedidoInteligencia = "saudavel" | "atencao" | "critico";

export type ResultadoInteligenciaPedidoDetalhe = {
  alertasSucesso: AlertaOperacionalInteligencia[];
  alertasAtencao: AlertaOperacionalInteligencia[];
  alertasCriticos: AlertaOperacionalInteligencia[];
  sugestoes: SugestaoOperacionalInteligencia[];
  saude: SaudePedidoInteligencia;
  /** Útil para testes / tooltips */
  horasDesdeReferenciaDevolucao?: number;
  horasDesdeReferenciaFaturamento?: number;
};

function parseIso(s?: string | null): Date | null {
  if (s == null || String(s).trim() === "") return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function horasEntre(inicio: Date, fim: Date): number {
  return (fim.getTime() - inicio.getTime()) / 3_600_000;
}

/** Melhor aproximação da entrada no status atual (histórico > updatedAt). */
function dataReferenciaStatusAtual(detalhe: IPedidoDetalhe): Date {
  const st = String(detalhe.status ?? "").toUpperCase();
  const hist = detalhe.historicoPedidos ?? [];
  const matches = hist.filter(
    (h) => h.statusDepois && String(h.statusDepois).toUpperCase() === st
  );
  if (matches.length) {
    const t = Math.max(...matches.map((m) => new Date(m.createdAt).getTime()));
    return new Date(t);
  }
  return (
    parseIso(detalhe.updatedAt) ??
    parseIso(detalhe.criadoEm) ??
    parseIso(detalhe.createAt) ??
    new Date()
  );
}

function inferirInicioPagamentoConfirmado(
  detalhe: IPedidoDetalhe,
  pagamentos: IPagamentoLoja[]
): Date {
  const cands: number[] = [];
  const pe = parseIso(detalhe.pagoEm);
  if (pe) cands.push(pe.getTime());
  for (const p of pagamentos) {
    if (p.status === "PAGO") {
      const t = parseIso(p.pagoEm ?? null);
      if (t) cands.push(t.getTime());
    }
  }
  for (const h of detalhe.historicoPedidos ?? []) {
    if (String(h.statusDepois ?? "").toUpperCase() === "PAGO") {
      cands.push(new Date(h.createdAt).getTime());
    }
  }
  if (!cands.length) {
    return dataReferenciaStatusAtual(detalhe);
  }
  return new Date(Math.max(...cands));
}

function statusFaturamentoNaoEmitido(detalhe: IPedidoDetalhe): boolean {
  const f = String(detalhe.statusFaturamento ?? "PENDENTE").toUpperCase();
  return f !== "EMITIDO";
}

function precisaMonitorarFaturamento(status: StatusPedido): boolean {
  const s = String(status ?? "").toUpperCase();
  return s === "PAGO" || s === "EM_PROCESSAMENTO" || s === "PROCESSANDO";
}

export function resolverInteligenciaPedidoDetalhe(
  detalhe: IPedidoDetalhe,
  pagamentos: IPagamentoLoja[]
): ResultadoInteligenciaPedidoDetalhe {
  const agora = new Date();
  const alertasSucesso: AlertaOperacionalInteligencia[] = [];
  const alertasAtencao: AlertaOperacionalInteligencia[] = [];
  const alertasCriticos: AlertaOperacionalInteligencia[] = [];
  const sugestoes: SugestaoOperacionalInteligencia[] = [];

  const st = String(detalhe.status ?? "").toUpperCase() as StatusPedido;
  const pagFalhou = pagamentos.some((p) => p.status === "FALHOU");

  let horasDesdeReferenciaDevolucao: number | undefined;
  let horasDesdeReferenciaFaturamento: number | undefined;

  if (st === "CANCELADO") {
    const est = resolveEstornoPedidoCanceladoUi(pagamentos);
    if (est === "pendente") {
      alertasCriticos.push({
        id: "estorno-financeiro-pendente",
        severidade: "error",
        titulo: "Estorno financeiro pendente",
        detalhe:
          "Pedido cancelado com pagamento ainda confirmado ou autorizado — conclua o estorno nas linhas de pagamento.",
      });
    }
  }

  if (pagFalhou) {
    alertasCriticos.push({
      id: "pag-falhou",
      severidade: "error",
      titulo: "Pagamento com falha",
      detalhe:
        "Há linha(s) de pagamento em status Falhou — o pedido pode precisar de correção ou cancelamento.",
    });
    sugestoes.push({
      id: "sug-cancelar-falha",
      texto: "Cancelar o pedido? Use as Ações do pedido se o cliente não concluirá o pagamento.",
      anchorId: "sec-acoes-pedido",
    });
  }

  if (
    precisaMonitorarFaturamento(detalhe.status) &&
    statusFaturamentoNaoEmitido(detalhe)
  ) {
    const inicio = inferirInicioPagamentoConfirmado(detalhe, pagamentos);
    const h = horasEntre(inicio, agora);
    horasDesdeReferenciaFaturamento = h;
    if (h >= SLA_HORAS_FATURAMENTO_CRITICO) {
      alertasCriticos.push({
        id: "sla-fat-critico",
        severidade: "error",
        titulo: "Faturamento em atraso (crítico)",
        detalhe: `Pagamento confirmado há cerca de ${Math.round(h)} h e o faturamento ainda não foi emitido.`,
      });
    } else if (h >= SLA_HORAS_FATURAMENTO_ATRASO) {
      alertasAtencao.push({
        id: "sla-fat-atraso",
        severidade: "warning",
        titulo: "Faturamento em atraso",
        detalhe: `Há cerca de ${Math.round(h)} h desde a confirmação do pagamento sem emissão fiscal.`,
      });
    }
  }

  if (st === "DEVOLUCAO_SOLICITADA") {
    const ref = dataReferenciaStatusAtual(detalhe);
    const h = horasEntre(ref, agora);
    horasDesdeReferenciaDevolucao = h;
    if (h >= SLA_HORAS_DEVOLUCAO_CRITICO) {
      alertasCriticos.push({
        id: "sla-dev-critico",
        severidade: "error",
        titulo: "Devolução pendente de confirmação (crítico)",
        detalhe: `A devolução foi solicitada há cerca de ${Math.round(h)} h sem confirmação.`,
      });
    } else if (h >= SLA_HORAS_DEVOLUCAO_PENDENTE) {
      alertasAtencao.push({
        id: "sla-dev-pendente",
        severidade: "warning",
        titulo: "Devolução pendente de confirmação",
        detalhe: `Há cerca de ${Math.round(h)} h desde a solicitação de devolução.`,
      });
    }
    sugestoes.push({
      id: "sug-confirma-dev",
      texto: "Se a análise estiver concluída, confirme a devolução nas Ações do pedido.",
      anchorId: "sec-acoes-pedido",
    });
  }

  const stOkEncerrado = st === "ENTREGUE" || st === "DEVOLVIDO";
  if (stOkEncerrado && !alertasCriticos.length && !alertasAtencao.length) {
    alertasSucesso.push({
      id: "fluxo-ok-encerrado",
      severidade: "success",
      titulo: "Pedido encerrado no fluxo",
      detalhe: "Sem pendências operacionais detectadas nesta tela.",
    });
  } else if (
    st === "CANCELADO" &&
    !alertasCriticos.length &&
    !alertasAtencao.length
  ) {
    alertasSucesso.push({
      id: "cancelado-ok",
      severidade: "success",
      titulo: "Pedido cancelado",
      detalhe: "Situação financeira coerente com o cancelamento (sem estorno pendente).",
    });
  } else if (
    !alertasCriticos.length &&
    !alertasAtencao.length &&
    !pagFalhou &&
    st !== "CANCELADO" &&
    st !== "AGUARDANDO_PAGAMENTO"
  ) {
    alertasSucesso.push({
      id: "sem-alerta",
      severidade: "success",
      titulo: "Operação dentro do esperado",
      detalhe: "Nenhum alerta de SLA ou pagamento foi detectado no momento.",
    });
  }

  let saude: SaudePedidoInteligencia = "saudavel";
  if (alertasCriticos.length) saude = "critico";
  else if (alertasAtencao.length) saude = "atencao";

  return {
    alertasSucesso,
    alertasAtencao,
    alertasCriticos,
    sugestoes,
    saude,
    horasDesdeReferenciaDevolucao,
    horasDesdeReferenciaFaturamento,
  };
}
